import { Button, CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { getAuth, GoogleAuthProvider, OAuthProvider, signInWithPopup, UserCredential } from 'firebase/auth';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectIsAnyOtherLoginOptionEnabled, selectIsEmailLoginEnabled } from '../../core/config-loader/store/config-loader-slice';
import { APP_ROUTE_PATHS } from '../../routing/appRoutes';
import LoginHeader from '../components/LoginHeader';
import LoginWithEmail from '../components/LoginWithEmail';
import { authActions, selectAuthState, selectIsAuthLoading, selectIsUserLoggedIn } from '../store/authSlice';

const LoginPage: React.FC = () => {
  const isAnyOtherLoginOptionEnabled = useSelector(selectIsAnyOtherLoginOptionEnabled);
  const isEmailLoginEnabled = useSelector(selectIsEmailLoginEnabled);

  const firebaseAuth = getAuth();
  const dispatch = useDispatch();

  const auth = useSelector(selectAuthState);
  const isLoading = useSelector(selectIsAuthLoading);
  const isLoggedIn = useSelector(selectIsUserLoggedIn);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [shouldEmailAndPasswordShow, setShouldEmailAndPasswordShow] = React.useState(isEmailLoginEnabled && !isAnyOtherLoginOptionEnabled);

  const handleLoginByEmailAndPassword = async (formData: { email: string; password: string }) => {
    dispatch(authActions.logInWithEmailAndPassword({ password: formData.password, email: formData.email, navigate }));
  };

  const handleLoginWithEmailButton = () => {
    setShouldEmailAndPasswordShow(true);
  };

  const handleLoginWithEmailBackButtonCLick = () => {
    setShouldEmailAndPasswordShow(false);
  };

  useEffect(() => {
    if (isLoggedIn) {
      navigate(APP_ROUTE_PATHS.GALLERY);
    }
  }, [isLoggedIn]);

  // TODO: we might can fix this later, but currently Redirect version of Firebase is not working (userCredential is always null)
  // useEffect(() => {
  //   getRedirectResult(firebaseAuth)
  //   .then(async(userCredential: UserCredential | null) => {
  //     if (!userCredential) {
  //       return;
  //     }
  //     const idToken = await userCredential.user.getIdToken();

  //     dispatch(authActions.loginToFirebaseByOAuthSuccess2({ idToken, navigate }));
  //   })
  //   // TODO: we might not need this
  //     .then((result: UserCredential | null) => {
  //       if (!result) {
  //         return;
  //       }
  //       if (result?.user.email) {
  //         dispatch(
  //           authActions.loginToFirebaseByOAuthSuccess({
  //             firebaseLoginResponseDTO: {
  //               localId: result?.user.uid,
  //               email: result?.user.email || '',
  //               displayName: result?.user.displayName || '',
  //               expiresIn: '',
  //               idToken: '',
  //               refreshToken: '',
  //               kind: '',
  //               registered: true,
  //             },
  //             navigate,
  //           })
  //         );
  //       }
  //     })
  //     .catch((error) => {
  //       dispatch(authActions.loginToFirebaseByOAuthError({ errorMessage: error.message }));
  //     });
  // }, [firebaseAuth, authActions]);

  const loginWithFirebase = (provider: GoogleAuthProvider | OAuthProvider) => {
    // dispatch(authActions.loginToFirebaseByOAuth());

    signInWithPopup(firebaseAuth, provider)
      .then(async (userCredential: UserCredential) => {
        const idToken = await userCredential.user.getIdToken();

        dispatch(authActions.loginToFirebaseByOAuthSuccess({ idToken, navigate }));
      })
      .catch((error) => {
        dispatch(authActions.loginToFirebaseByOAuthError({ errorMessage: error.message }));
      });
  };

  // const handleLoginWithGoogleButton = () => {
  //   loginWithFirebase(new GoogleAuthProvider());
  // };

  const handleLoginWithMicrosoftButton = () => {
    loginWithFirebase(new OAuthProvider('microsoft.com'));
  };

  const loginWithEmailAndPassword = () => {
    if (shouldEmailAndPasswordShow) {
      return (
        <LoginWithEmail
          handleLoginByEmailAndPassword={handleLoginByEmailAndPassword}
          handleBackButtonClick={handleLoginWithEmailBackButtonCLick}
          isLoading={isLoading}
          errors={auth.errors}
          isAnyOtherLoginOptionEnabled={isAnyOtherLoginOptionEnabled}
        />
      );
    } else if (isEmailLoginEnabled) {
      return (
        <Container sx={{ width: '350px' }}>
          <Button disabled={isLoading} onClick={handleLoginWithEmailButton} fullWidth variant="contained" sx={{ mt: 5, mb: 2 }}>
            {t('LOGIN.LOGIN_WITH_EMAIL_BUTTON')}
          </Button>
        </Container>
      );
    }
  };

  const otherLoginOptions = () => {
    if (isAnyOtherLoginOptionEnabled && !shouldEmailAndPasswordShow) {
      return (
        <Container sx={{ width: '350px' }}>
          {/* <Button disabled={isLoading} onClick={handleLoginWithGoogleButton} fullWidth variant="contained" sx={{ mb: 2 }}>
            {t('LOGIN.LOGIN_WITH_GOOGLE_BUTTON')}
          </Button> */}
          <Button disabled={isLoading} onClick={handleLoginWithMicrosoftButton} fullWidth variant="contained">
            {isLoading && <CircularProgress size={20} sx={{ marginRight: '4px' }} />}
            {t('LOGIN.LOGIN_WITH_MICROSOFT_BUTTON')}
          </Button>
        </Container>
      );
    }
  };

  return (
    <>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <LoginHeader isLoading={isLoading} errors={auth.errors} />

          {loginWithEmailAndPassword()}
          {otherLoginOptions()}
        </Box>
      </Container>
    </>
  );
};

export default LoginPage;
